import { fetchRipplingHandoverToken } from '@/rippling/actions/fetchRipplingHandoverToken';
import { RIPPLING_MIGRATION_LOGIN_URL } from '@/rippling/util/constants';
import { getToken, getTokenInfo } from '@/shared/auth/auth';

const RIPPLING_HANDOVER_ORIGIN = 'https://app.rippling.com';

/**
 * Attempts a seamless session handover to Rippling:
 * 1. Exchanges the user's WIW token for a single-use nonce via the WIW login service.
 *    The login service calls Rippling's /auth_ext/web-bridge/register server-to-server,
 *    so the Rippling access token never travels through the browser.
 * 2. Navigates to app.rippling.com/api/auth_ext/web-bridge?nonce=... where rippling-main
 *    redeems the nonce, injects the session server-side, and redirects into the app.
 *
 * Falls back to the standard Rippling sign-in URL on any error.
 */
export async function navigateToRippling({ newTab = false }: { newTab?: boolean } = {}): Promise<void> {
  const token = getToken();

  if (!token) {
    console.error('[navigateToRippling] Blocked: missing token');
    window.location.assign(RIPPLING_MIGRATION_LOGIN_URL);
    return;
  }

  const tokenInfo = getTokenInfo();

  if (tokenInfo.isLimited() || tokenInfo.isExpired()) {
    console.error('[navigateToRippling] Blocked: limited or expired token');
    window.location.assign(RIPPLING_MIGRATION_LOGIN_URL);
    return;
  }

  try {
    const { nonce, role_id } = await fetchRipplingHandoverToken();

    const handoverUrl = new URL('/api/auth_ext/web-bridge', RIPPLING_HANDOVER_ORIGIN);
    handoverUrl.searchParams.set('nonce', nonce);
    handoverUrl.searchParams.set('role_id', role_id);
    handoverUrl.searchParams.set('redirect_uri', '/time-products/dashboard/schedules');

    if (newTab) {
      window.open(handoverUrl.toString(), '_blank', 'noopener');
    } else {
      window.location.assign(handoverUrl.toString());
    }
  } catch (error) {
    console.error('[navigateToRippling] Handover failed, falling back to sign-in', error);
    window.location.assign(RIPPLING_MIGRATION_LOGIN_URL);
  }
}
